import { apiClient } from "./client";
import { env } from "@/lib/env";
import { waNumber } from "@/lib/phone";

/**
 * The sales pitch, from the portal's side.
 *
 * A pitch is built on the website (/sales-pitch) and saved with whatever
 * identifies the shop: its Google place id, and the id of the lead the builder
 * was opened from. Both are asked for, because imported research rows carry
 * Google's CID and never a place id — for those the lead id is the only thing
 * the two sides share.
 */
export type PitchLink = {
  slug: string;
  businessName: string;
  placeId: string;
  leadId: string;
  views: number;
  lastViewedAt: string | null;
  createdAt: string;
  marketCategory: string;
  /** What the pitch is about, and the numbers it makes its case with. */
  goal?: "reviews" | "followers";
  reviews?: number | null;
  targetReviews?: number | null;
  followers?: number | null;
};

export type PitchLinks = { byPlace: Record<string, PitchLink>; byLead: Record<string, PitchLink> };

export async function getPitchLinks(placeIds: string[], leadIds: string[]): Promise<PitchLinks> {
  const ids = placeIds.filter(Boolean);
  const leads = leadIds.filter(Boolean);
  if (!ids.length && !leads.length) return { byPlace: {}, byLead: {} };
  // apiClient.get hands back the JSON body itself, not the axios envelope.
  const body = (await apiClient.get(`/demo-links/by-place`, {
    params: { ids: ids.join(","), leadIds: leads.join(",") },
  })) as { data?: Partial<PitchLinks> };
  return { byPlace: body?.data?.byPlace ?? {}, byLead: body?.data?.byLead ?? {} };
}

/**
 * The builder, with the lead already filled in.
 *
 * With a linked listing the shop is read straight from Google and only the
 * trade is left to confirm; without one the name and area are handed over as a
 * search, so the shop is picked from Google's own results. Either way the lead
 * id travels with it, which is how the saved pitch finds its way back here.
 */
export function buildPitchUrl(lead: {
  id: string;
  placeId?: string;
  businessName: string;
  category?: string;
  businessType?: string;
  area?: string;
  city?: string;
}): string {
  const where = lead.area || lead.city || "";
  const q = new URLSearchParams({
    leadId: lead.id,
    name: lead.placeId ? lead.businessName : [lead.businessName, where].filter(Boolean).join(" "),
  });
  if (lead.placeId) q.set("placeId", lead.placeId);
  // Google's own label for this business ("Skin care clinic") says far more
  // than the bucket it was imported under ("Clinics & dental"), and the pitch
  // knows how to read it. The bucket is the fallback.
  const trade = lead.businessType || lead.category;
  if (trade) q.set("category", trade);
  return `${env.siteUrl}/sales-pitch?${q.toString()}`;
}

/**
 * The link to open or send, with the number it is about in it.
 *
 * WhatsApp keeps one preview per URL and reuses it, so a pitch rebuilt with a
 * different figure -- a trade corrected, the footfall slider moved -- would
 * otherwise be sent with the card it was first previewed as. The target makes
 * a rebuilt pitch a URL WhatsApp has not seen; an unchanged one keeps the same
 * link. The page itself ignores the query.
 */
export function pitchViewUrl(slug: string, pitch?: Pick<PitchLink, "goal" | "targetReviews" | "followers">): string {
  const base = `${env.siteUrl}/for/${slug}`;
  const stamp = pitch?.goal === "followers" ? pitch?.followers : pitch?.targetReviews;
  return stamp ? `${base}?v=${stamp}` : base;
}

/** What PK sends after the call, the same sentence the link's own preview shows. */
export function pitchWhatsAppUrl(
  slug: string,
  businessName: string,
  phone?: string,
  pitch?: PitchLink,
): string {
  const url = pitchViewUrl(slug, pitch);
  const n = (v?: number | null) => Number(v ?? 0).toLocaleString("en-IN");
  const text =
    pitch?.goal === "followers"
      ? `Make every customer at ${businessName} follow you on Instagram. Watch a 30-second demo:\n${url}`
      : pitch?.reviews != null && pitch?.targetReviews
      ? `Fastest way to grow Google reviews for ${businessName} — from ${n(pitch.reviews)} to ${n(
          pitch.targetReviews,
        )} in a year. Watch a 30-second demo:\n${url}`
      : `Fastest way to grow Google reviews for ${businessName}. Watch a 30-second demo:\n${url}`;
  return `https://wa.me/${waNumber(phone || "")}?text=${encodeURIComponent(text)}`;
}

/**
 * A shop someone ran the demo on.
 *
 * The same row a pitch is saved as -- `origin` is the only thing that says
 * who made it: "pk" on a call, "self" from a landing page's demo button.
 */
export type DemoLinkRow = PitchLink & {
  origin?: "pk" | "self";
  area?: string;
  rating?: number | null;
  views: number;
};

/**
 * GET /v1/demo-links.
 *
 * `origin` defaults to "pk" on the server -- the pitch list -- so Demos
 * checked has to ask for "self" explicitly. Legacy rows carry no origin at
 * all and count as PK's, which is what they were.
 */
export async function listDemoLinks(
  origin: "pk" | "self" | "all",
  pageNumber = 1,
  pageLimit = 50
): Promise<{ items: DemoLinkRow[]; total: number }> {
  const body = (await apiClient.get(`/demo-links`, {
    params: { origin, pageNumber, pageLimit },
  })) as { data?: DemoLinkRow[]; total?: number };
  return { items: body?.data ?? [], total: body?.total ?? 0 };
}

